import React, { useEffect, useState, useCallback } from 'react';
import { X, Share, Plus, Download } from 'lucide-react';
import useIsMobile from '../hooks/useIsMobile';
import { useTheme } from '../contexts/ThemeContext';

const DISMISS_KEY = 'iat:pwaInstallDismissed';

/** Já está instalado / rodando como app? */
function isStandalone() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)')?.matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  if (typeof navigator === 'undefined') return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
}

/**
 * Banner discreto "Adicionar à tela inicial".
 * - Android/Chrome: usa o evento beforeinstallprompt (instalação nativa).
 * - iOS Safari: mostra as instruções (Compartilhar → Adicionar à Tela de Início).
 * Mobile-only, dispensável e lembra a dispensa.
 */
export default function PwaInstallPrompt() {
  const isMobile = useIsMobile();
  const { isDarkMode } = useTheme();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);

  const alreadyDismissed = () => {
    try { return window.localStorage.getItem(DISMISS_KEY) === '1'; } catch (_) { return false; }
  };

  useEffect(() => {
    if (!isMobile || isStandalone() || alreadyDismissed()) return undefined;

    // Android/Chrome
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    // iOS Safari: não há evento — mostra ajuda manual após um instante
    let iosTimer;
    if (isIOS()) {
      iosTimer = setTimeout(() => setVisible(true), 1500);
    }

    const onInstalled = () => dismiss();
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  const dismiss = useCallback(() => {
    setVisible(false);
    setShowIosHelp(false);
    try { window.localStorage.setItem(DISMISS_KEY, '1'); } catch (_) {}
  }, []);

  const handleInstall = useCallback(async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      try { await deferredPrompt.userChoice; } catch (_) {}
      setDeferredPrompt(null);
      dismiss();
    } else if (isIOS()) {
      setShowIosHelp((v) => !v);
    }
  }, [deferredPrompt, dismiss]);

  if (!isMobile || !visible) return null;

  const theme = {
    card: isDarkMode ? '#232329' : '#FFFFFF',
    border: isDarkMode ? '#2D2D35' : 'rgba(11, 11, 18, 0.12)',
    text: isDarkMode ? '#FAFAFB' : '#0E0E12',
    muted: isDarkMode ? '#9A9AA5' : '#4E4E58',
    accent: '#F59E0B',
  };

  return (
    <div
      style={{
        position: 'fixed',
        left: 12,
        right: 12,
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
        zIndex: 5000,
        background: theme.card,
        border: `1px solid ${theme.border}`,
        borderRadius: 16,
        boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
        padding: 14,
      }}
      role="dialog"
      aria-label="Instalar aplicativo"
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div
          style={{
            width: 38, height: 38, borderRadius: 10, flexShrink: 0,
            background: 'rgba(245, 158, 11, 0.15)', color: theme.accent,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <Download size={20} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: theme.text }}>Instalar o app IAT</div>
          <div style={{ fontSize: 12.5, color: theme.muted, marginTop: 2, lineHeight: 1.35 }}>
            Acesso rápido pela tela inicial, em tela cheia.
          </div>
        </div>
        <button
          onClick={dismiss}
          aria-label="Dispensar"
          style={{ background: 'none', border: 'none', color: theme.muted, cursor: 'pointer', padding: 2, flexShrink: 0 }}
        >
          <X size={18} />
        </button>
      </div>

      {showIosHelp && isIOS() && (
        <div style={{ marginTop: 10, fontSize: 12.5, color: theme.muted, lineHeight: 1.5 }}>
          No Safari, toque em <Share size={13} style={{ display: 'inline', verticalAlign: '-2px' }} /> (Compartilhar)
          e depois em <strong style={{ color: theme.text }}>Adicionar à Tela de Início</strong>{' '}
          <Plus size={13} style={{ display: 'inline', verticalAlign: '-2px' }} />.
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button
          onClick={handleInstall}
          style={{
            flex: 1, height: 40, borderRadius: 10, border: 'none', cursor: 'pointer',
            background: theme.accent, color: '#0E0E12', fontWeight: 700, fontSize: 14,
          }}
        >
          {deferredPrompt ? 'Instalar' : (isIOS() ? (showIosHelp ? 'Ocultar instruções' : 'Como instalar') : 'Instalar')}
        </button>
        <button
          onClick={dismiss}
          style={{
            height: 40, padding: '0 16px', borderRadius: 10, cursor: 'pointer',
            background: 'transparent', color: theme.muted, border: `1px solid ${theme.border}`, fontWeight: 600, fontSize: 14,
          }}
        >
          Agora não
        </button>
      </div>
    </div>
  );
}
